import { Routes, Route, Navigate, useLocation, useNavigationType } from 'react-router-dom';
import { useState, useEffect, lazy, Suspense } from 'react';
const FollowUpCalculatorPage = lazy(() => import('./pages/FollowUpCalculatorPage'));
const ThankYouPage = lazy(() => import('./pages/ThankYouPage'));
import Header from './components/Header';
import VapiWidget from './components/VapiWidget';
import ScrollProgress from './components/ui/ScrollProgress';
import StickyMobileCTA from './components/ui/StickyMobileCTA';
import ScrollDepthCTA from './components/ui/ScrollDepthCTA';
const HomePage = lazy(() => import('./pages/HomePage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const WhatDoIDoPage = lazy(() => import('./pages/WhatDoIDoPage'));
const WebsitesPage = lazy(() => import('./pages/WebsitesPage'));
const CanadianRealEstatePage = lazy(() => import('./pages/CanadianRealEstatePage'));
const ROICalculatorPage = lazy(() => import('./pages/ROICalculatorPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const GlossaryPage = lazy(() => import('./pages/GlossaryPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const AdminLoginPage = lazy(() => import('./pages/admin/AdminLoginPage'));
const AdminBlogPage = lazy(() => import('./pages/admin/AdminBlogPage'));
const AdminPostEditorPage = lazy(() => import('./pages/admin/AdminPostEditorPage'));
const AdminVideoUploadPage = lazy(() => import('./pages/admin/AdminVideoUploadPage'));
const AdminLeadsPage = lazy(() => import('./pages/admin/AdminLeadsPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
import Footer from './components/Footer';
import ErrorBoundary from './components/ui/ErrorBoundary';
import { ProductSelectionProvider } from './contexts/ProductSelectionContext';
import { VapiProvider } from './contexts/VapiContext';

// Keep the generated page visible while its interactive route bundle loads.
const initialPath = typeof window === 'undefined' ? null : window.location.pathname;
const initialMarkup = typeof document === 'undefined' ? '' :
  (document.getElementById('main-content')?.innerHTML || document.getElementById('root')?.innerHTML || '');

function PageLoading() {
  const location = useLocation();
  if (initialMarkup && location.pathname === initialPath) {
    return <div aria-busy="true" className="pointer-events-none" dangerouslySetInnerHTML={{ __html: initialMarkup }} />;
  }
  return <div role="status" className="min-h-[50vh] flex items-center justify-center text-gray-600">Loading page…</div>;
}

export default function App() {
  const [isAIActive, setIsAIActive] = useState(false);
  const location = useLocation();
  const navigationType = useNavigationType();
  useEffect(() => {
    if (navigationType !== 'POP' && !location.hash) window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location.pathname, location.hash, navigationType]);
  const routePath = location.pathname.replace(/\/+$/, '') || '/';
  const isHome = routePath === '/';
  const isReadingPage = ['/about', '/canadian-real-estate', '/roi-calculator', '/faq', '/blog'].includes(routePath) || routePath.startsWith('/blog/');
  const isAdmin = routePath.startsWith('/admin');


  const isBook = routePath === '/book' || routePath === '/book/thank-you' || routePath === '/book/free-breakdown';

  if (isBook) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<PageLoading />}>
        <Routes>
          <Route path="/book" element={<FollowUpCalculatorPage />} />
          <Route path="/book/free-breakdown" element={<FollowUpCalculatorPage />} />
          <Route path="/book/thank-you" element={<ThankYouPage />} />
        </Routes>
        </Suspense>
      </ErrorBoundary>
    );
  }

  if (isAdmin) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<PageLoading />}>
        <Routes>
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin/blog" element={<AdminBlogPage />} />
          <Route path="/admin/blog/new" element={<AdminPostEditorPage />} />
          <Route path="/admin/blog/edit/:id" element={<AdminPostEditorPage />} />
          <Route path="/admin/video-upload" element={<AdminVideoUploadPage />} />
          <Route path="/admin/leads" element={<AdminLeadsPage />} />
        </Routes>
        </Suspense>
      </ErrorBoundary>
    );
  }

  return (
    <VapiProvider>
    <ProductSelectionProvider>
      <div className="pbs-public min-h-screen flex flex-col bg-white">
        <ErrorBoundary>
          <ScrollProgress />
          <a href="#main-content" className="skip-link">Skip to main content</a>
          <Header />
          <main id="main-content" tabIndex={-1} className={`flex-grow pt-[calc(4rem+env(safe-area-inset-top))] sm:pt-[calc(5rem+env(safe-area-inset-top))]`}>
            <Suspense fallback={<PageLoading />}>
        <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/ai-automation-plan" element={<Navigate to="/" replace />} />
              <Route path="/what-do-i-do" element={<WhatDoIDoPage />} />
              <Route path="/websites" element={<WebsitesPage />} />
              <Route path="/canadian-real-estate" element={<CanadianRealEstatePage />} />
              <Route path="/roi-calculator" element={<ROICalculatorPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/glossary" element={<GlossaryPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
        </Suspense>
          </main>
          <Footer />

          {/* Mobile Sticky CTA */}
          {!isHome && routePath !== '/contact' && !isAIActive && <StickyMobileCTA />}

          {/* Desktop scroll-depth lead capture */}
          {!isHome && !isReadingPage && routePath !== '/contact' && <ScrollDepthCTA />}

          {/* Sticky Vapi Widget, desktop only to avoid collision with StickyMobileCTA */}
          {routePath !== '/contact' && <div className={`${isAIActive ? 'block' : (isHome || isReadingPage) ? 'hidden' : 'hidden lg:block'} fixed bottom-6 right-6 z-50`}>
            <div className="relative">
              <div className="absolute -inset-3 bg-gradient-to-r from-[#d4a10d] to-[#b8940b] rounded-full opacity-30 blur-md" />
              <div className="relative flex flex-col items-center gap-1">
                <VapiWidget
                  apiKey={import.meta.env.VITE_VAPI_API_KEY}
                  assistantId={import.meta.env.VITE_VAPI_ASSISTANT_ID}
                  buttonText="Test our AI"
                  onConnectionChange={setIsAIActive}
                  className=""
                />
                <span className="text-[10px] font-semibold text-gray-500 tracking-wide whitespace-nowrap select-none">
                  {isAIActive ? 'Voice demo active' : 'Test our AI'}
                </span>
              </div>
            </div>
          </div>}
        </ErrorBoundary>
      </div>
    </ProductSelectionProvider>
    </VapiProvider>
  );
}
